import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { PromotePoaDialog } from "@/features/eservices/components/promote-poa-dialog";
import { RedirectIrsDialog } from "@/features/eservices/components/redirect-irs-dialog";
import { dismissPoas, getPendingPoas } from "@/features/eservices/api";
import type { PoaPendingRow } from "@/features/eservices/types";

export function PoaSubmissionPage() {
  const [rows, setRows] = useState<PoaPendingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [redirectOpen, setRedirectOpen] = useState(false);
  const [promote, setPromote] = useState<PoaPendingRow | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    getPendingPoas()
      .then(setRows)
      .catch(() => toast.error("Could not load pending POAs."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const bulkDismiss = async () => {
    if (selected.size === 0) return;
    try {
      await dismissPoas([...selected]);
      toast.success("Dismissed. The documents stay in Documents.");
      setSelected(new Set());
      refresh();
    } catch {
      toast.error("Dismiss failed.");
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/eservices"
            className="text-muted-foreground mb-1 flex items-center gap-1 text-sm hover:underline"
          >
            <ArrowLeft className="size-3" /> e-Services
          </Link>
          <h1 className="text-foreground text-2xl font-semibold">POA Submission</h1>
          <p className="text-muted-foreground text-sm">
            Electronically-signed 2848/8821 forms awaiting IRS upload.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bulkDismiss} disabled={selected.size === 0}>
            Dismiss selected
          </Button>
          <Button onClick={() => setRedirectOpen(true)}>Go to POA Upload</Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No signed POAs are waiting to be uploaded.
        </p>
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Client</TableHead>
                <TableHead>Form</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggle(r.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{r.clientName}</TableCell>
                  <TableCell>{r.form}</TableCell>
                  <TableCell className="text-muted-foreground">{r.name}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPromote(r)}
                    >
                      Monitor CAF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <RedirectIrsDialog
        open={redirectOpen}
        onOpenChange={setRedirectOpen}
        target="poa"
      />
      <PromotePoaDialog
        row={promote}
        onOpenChange={(open) => !open && setPromote(null)}
        onPromoted={() => {
          setPromote(null);
          toast.success("CAF monitoring started.");
        }}
      />
    </div>
  );
}
